import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'

function PortalPrivacySettings({ teamId }) {
  const [settings, setSettings] = useState({
    roster_public: false,
    files_public: false,
    snacks_public: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [teamId])

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('portal_settings')
        .select('*')
        .eq('team_id', teamId)
        .single()

      if (error) throw error
      if (data) {
        setSettings({
          roster_public: data.roster_public,
          files_public: data.files_public,
          snacks_public: data.snacks_public,
        })
      }
    } catch (err) {
      console.error('Error fetching portal settings:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setSuccess(false)
    try {
      const { error } = await supabase
        .from('portal_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq('team_id', teamId)

      if (error) throw error
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving privacy settings:', err)
      alert('Failed to save privacy settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8 text-brand-slate">
          Loading privacy settings...
        </div>
      </Card>
    )
  }

  const privacyOptions = [
    {
      key: 'roster_public',
      title: 'Team Roster',
      description: 'Show player names (First L. format only), jersey numbers, and positions on the public portal',
    },
    {
      key: 'files_public',
      title: 'Uploaded Files',
      description: 'Allow parents to view and download files you\'ve uploaded',
    },
    {
      key: 'snacks_public',
      title: 'Snack Schedule',
      description: 'Display who is bringing snacks for each game on the public portal',
    },
  ]

  return (
    <Card>
      <div className="mb-6">
        <h2 className="font-heading text-xl font-bold mb-2">
          Portal Privacy Settings
        </h2>
        <p className="text-sm text-brand-slate">
          Control what information is visible on your public team portal. All settings default to private for safety.
        </p>
      </div>

      <div className="space-y-6">
        {privacyOptions.map((option) => (
          <div
            key={option.key}
            className="flex items-start justify-between gap-4 p-4 border border-border rounded-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-medium text-brand-charcoal">
                  {option.title}
                </h3>
                {!settings[option.key] && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                    🔒 Private
                  </span>
                )}
                {settings[option.key] && (
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">
                    👁 Public
                  </span>
                )}
              </div>
              <p className="text-sm text-brand-slate">
                {option.description}
              </p>
            </div>
            <button
              onClick={() => handleToggle(option.key)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                settings[option.key] ? 'bg-brand-green' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings[option.key] ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-900">
          <strong>Privacy Note:</strong> Even when enabled, roster shows only "First L." format (e.g., "John D."). Full names are never displayed publicly.
        </p>
      </div>

      {success && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          Privacy settings saved successfully!
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Privacy Settings'}
        </Button>
      </div>
    </Card>
  )
}

export default PortalPrivacySettings


